import { prefersReducedMotion } from '../lib/platform';
import s from './RouteArc.module.css';

export type Pt = [number, number];

/**
 * Control point for the contrail arc between two screen points: a quadratic curve that
 * bows away from the chord, upward, by a share of its length.
 */
export function arcControl(a: Pt, b: Pt, bulge = 0.22): Pt {
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular, flipped so the arc bows upward on screen.
  let nx = -dy / len;
  let ny = dx / len;
  if (ny > 0) {
    nx = -nx;
    ny = -ny;
  }
  return [mx + nx * len * bulge, my + ny * len * bulge];
}

export function arcPath(a: Pt, b: Pt, bulge = 0.22): string {
  const c = arcControl(a, b, bulge);
  return `M${a[0].toFixed(1)} ${a[1].toFixed(1)} Q${c[0].toFixed(1)} ${c[1].toFixed(1)} ${b[0].toFixed(1)} ${b[1].toFixed(1)}`;
}

/** Point at t (0..1) along the arc, for placing a plane mid-flight. */
export function pointOnArc(a: Pt, b: Pt, t: number, bulge = 0.22): Pt {
  const c = arcControl(a, b, bulge);
  const u = 1 - t;
  return [
    u * u * a[0] + 2 * u * t * c[0] + t * t * b[0],
    u * u * a[1] + 2 * u * t * c[1] + t * t * b[1],
  ];
}

interface RouteArcProps {
  from: Pt;
  to: Pt;
  bulge?: number;
  /** Draw itself when it appears. */
  draw?: boolean;
  /** Portion drawn solid (flight progress), 0..1. Omit for a full arc. */
  progress?: number;
  className?: string;
  strokeWidth?: number;
  dots?: boolean;
}

export function RouteArc({
  from,
  to,
  bulge = 0.22,
  draw,
  progress,
  className,
  strokeWidth = 1.5,
  dots = true,
}: RouteArcProps) {
  const d = arcPath(from, to, bulge);
  const animate = draw && !prefersReducedMotion();
  return (
    <g className={`${s.arc} ${className ?? ''}`}>
      {progress != null && <path d={d} className={s.track} strokeWidth={strokeWidth} fill="none" />}
      <path
        d={d}
        pathLength={1}
        className={`${s.line} ${animate ? s.draw : ''}`}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={progress != null ? `${Math.max(0, Math.min(1, progress))} 1` : undefined}
      />
      {dots && (
        <>
          <circle cx={from[0]} cy={from[1]} r={strokeWidth * 2} className={s.dot} />
          <circle cx={to[0]} cy={to[1]} r={strokeWidth * 2} className={s.dot} />
        </>
      )}
    </g>
  );
}
